import type { Vec3 } from '../utils/math';

/**
 * Moving hazards (phase 2): crane loads (SEG_09), excavator (SEG_10), forklift (SEG_11) and the
 * slow truck (SEG_03 variant). Times in seconds, lengths in meters, angles in degrees.
 *
 * Every hazard is scheduled backwards from the runner's arrival at its key line (ETA from the
 * current run speed): `readyLead` = seconds before the runner arrives at which the hazard has
 * reached its dangerous state. It is always more than reaction + two lane changes (0.78 s), so a
 * telegraphed hazard can always be read and dodged – at 8 m/s as well as at 18 m/s.
 *
 * Rig measurements (`rig`, `body`) are taken from the Blender exports (public/assets/models) –
 * gameplay colliders are derived from them, the GLB collision proxies are not used at runtime.
 */
export interface CraneLoadRig {
  /** Rope top (OBS_CRANE_LOAD_*_PIVOT_SWING) above the load's origin. */
  readonly pivotHeight: number;
  /** Underside of the load above its origin in the rest pose. */
  readonly loadBottom: number;
  /** Load body: x across the track, y up, z along the track. */
  readonly load: { readonly width: number; readonly height: number; readonly depth: number };
  /** Sling, hook block and rope above the load are solid this far up – the load cannot be jumped over. */
  readonly guardHeight: number;
  /**
   * Half width (across the track, from the load's centre) over which that sling stands – only where the
   * model shows it (SEGMENT_STANDARD "Sprungehrlichkeit"). Default: the whole load (a chain sling over a block).
   */
  readonly guardHalfWidth?: number;
}

/** One box of a vehicle's gameplay collider in vehicle space (+z = vehicle front, origin = ground centre). */
export interface VehiclePart {
  readonly center: Vec3;
  readonly size: Vec3;
  /**
   * Forklift: rides on the fork carriage and is lifted with the forks; `load` only while the forklift carries
   * its pallet.
   */
  readonly carriage?: 'forks' | 'load';
}

export interface VehicleConfig {
  /** Driving speed towards the runner (m/s) – adds to the runner's speed. */
  readonly speed: number;
  /** Time to reach `speed` from standstill. */
  readonly accelerationTime: number;
  /** Braking distance before the stop position. */
  readonly brakeDistance: number;
  /** Horn + beacon before it starts driving. */
  readonly telegraph: number;
  /** Turn signal this long before a lane change. */
  readonly signalLead: number;
  /** Travel (m) over which a lane change happens. */
  readonly laneChangeLength: number;
  /** Visual steering angle at the steepest point of a lane change. */
  readonly maxSteerDegrees: number;
  /**
   * Gameplay collider: boxes along the model's outline, each only as high as the model there – a low fork
   * with its pallet is no wall up to the cab roof (SEGMENT_STANDARD "Sprungehrlichkeit").
   */
  readonly body: readonly VehiclePart[];
  /** Front face (fork tips / bumper) and rear face from the origin. */
  readonly front: number;
  readonly rear: number;
}

export interface HazardsConfig {
  /** OBS_CRANE_LOAD_A: two 6 m steel beams across all lanes, lowered to slide height and swinging. */
  readonly craneBeam: {
    readonly rig: CraneLoadRig;
    /** Underside of the beams while hoisted (idle) – above every jump. */
    readonly hoistedClearance: number;
    /** Warning (horn, beacons) before the crane lowers the load. */
    readonly telegraph: number;
    readonly lowerDuration: number;
    readonly readyLead: number;
    /** Pendulum swing along the track while lowered. */
    readonly swingDegrees: number;
    readonly swingPeriod: number;
    /** Gentle sway while hoisted (visual). */
    readonly idleSwayDegrees: number;
    readonly idleSwayPeriod: number;
  };
  /** OBS_CRANE_LOAD_B: precast concrete block lowered into one lane (dodge). */
  readonly craneBlock: {
    readonly rig: CraneLoadRig;
    readonly hoistedClearance: number;
    /** Underside of the lowered block – hangs just above the road. */
    readonly loweredClearance: number;
    readonly telegraph: number;
    readonly dropDuration: number;
    readonly readyLead: number;
    /** Rebound after the drop (visual and collider). */
    readonly bounceHeight: number;
    readonly bounceDuration: number;
    readonly idleSwayDegrees: number;
    readonly idleSwayPeriod: number;
    readonly idleSpinDegreesPerSecond: number;
  };
  /**
   * OBS_CRANE_LOAD_B as a pendulum (SEG_09): lowered over the track centre after the warning, then
   * swinging across all three lanes on its rope – be in a lane it is not over when you pass.
   */
  readonly craneSwing: {
    readonly rig: CraneLoadRig;
    /** Underside of the load while hoisted (idle) – above every double jump. */
    readonly hoistedClearance: number;
    /** Underside of the hanging load at the bottom of the swing (rest angle). */
    readonly hangClearance: number;
    /** Warning (beeps, toast) before the crane lowers the load; the swing starts building with it. */
    readonly telegraph: number;
    readonly lowerDuration: number;
    /** Lowered and swinging at full width this long before the runner arrives. */
    readonly readyLead: number;
    /** Lateral distance of the load's centre from the track centre at the turning points. */
    readonly reach: number;
    /** The trolley (rope top) travels this far either side of the centre in step with the load – the rest is rope angle. */
    readonly trolleyTravel: number;
    /** Full swing left → right → left. */
    readonly swingPeriod: number;
    /**
     * The swing is locked to the ETA at the warning, taken at that moment's speed – the run keeps accelerating
     * a little. The swing clock creeps towards the arrival the runner is heading for by at most `rate` seconds
     * per second, and only while it is off by less than `window` seconds (a runner held up by a hit is not
     * waited for – the swing runs on freely).
     */
    readonly relock: { readonly rate: number; readonly window: number };
    /** The rope stretches when the lowering stops: the load dips by `height` and settles within `duration`. */
    readonly rebound: { readonly height: number; readonly duration: number };
    /** Slow twist of the load about its rope while it swings (visual and collider). */
    readonly twistDegrees: number;
    readonly twistPeriod: number;
    /** Small swing along the track on top of the lateral one (the load never moves in a perfect plane). */
    readonly wobbleDegrees: number;
    readonly wobblePeriod: number;
    /** Gentle sideways sway and slow turn while hoisted (visual). */
    readonly idleSwayDegrees: number;
    readonly idleSwayPeriod: number;
    readonly idleSpinDegreesPerSecond: number;
    /**
     * Lanes the load touches within ± this many seconds of the runner's arrival are blocked – the
     * sequence validator gets exactly one of them (the variant's lane), the validator checks that.
     */
    readonly blockWindow: number;
  };
  /** VEH_EXCAVATOR_A beside the track: slews over the outer lane and dumps a rubble heap. */
  readonly excavator: {
    /** Arm geometry of the rig (VEH_EXCAVATOR_A_PIVOT_*), arm plane: forward / up. */
    readonly rig: {
      /** The upper carriage's rear sweeps this far around the slew centre – it must stay off the track. */
      readonly tailRadius: number;
      readonly upperHeight: number;
      /** Boom pivot relative to the slew pivot; `side` = to the right of the arm direction. */
      readonly boomPivot: { readonly forward: number; readonly up: number; readonly side: number };
      /** Stick pivot relative to the boom pivot (rest pose). */
      readonly stickPivot: { readonly forward: number; readonly up: number };
      /** Bucket pivot relative to the stick pivot (rest pose). */
      readonly bucketPivot: { readonly forward: number; readonly up: number };
      /** Bucket outline relative to its pivot (rest pose): forward back…front, up bottom…top. */
      readonly bucket: { readonly back: number; readonly front: number; readonly bottom: number; readonly top: number; readonly width: number };
    };
    readonly telegraph: number;
    readonly swingIn: number;
    readonly lower: number;
    readonly dump: number;
    /** Share of the dump before the rubble lands – the heap grows over the rest of it. */
    readonly heapDelay: number;
    readonly hold: number;
    readonly swingOut: number;
    /** Blend back into the idle digging loop. */
    readonly settle: number;
    /** The heap lies in the lane this long before the runner arrives. */
    readonly readyLead: number;
    /** Bucket pivot while carrying (high above fence and lanes) and while dumping over the lane. */
    readonly carryPivotHeight: number;
    readonly carryReach: number;
    readonly dumpPivotHeight: number;
    /** Bucket orientation (positive = curled towards the machine). */
    readonly carryCurlDegrees: number;
    readonly dumpCurlDegrees: number;
    /** Idle digging beside the track, arm turned away from the dump direction. */
    readonly idle: {
      readonly slewAwayDegrees: number;
      readonly reach: { readonly min: number; readonly max: number };
      readonly pivotHeight: { readonly min: number; readonly max: number };
      readonly curlDegrees: { readonly min: number; readonly max: number };
      readonly period: number;
    };
    /** Stick above the bucket: the lane under the bucket is blocked this far up. */
    readonly columnHeight: number;
    /** Rubble heap left in the lane (jumpable). */
    readonly heap: { readonly width: number; readonly height: number; readonly depth: number };
  };
  readonly forklift: VehicleConfig & {
    /** Fork carriage lift for HIGH forks, carriage-to-load-top height, mast tilt while driving. */
    readonly forks: { readonly highLift: number; readonly loadTop: number; readonly tiltBackDegrees: number };
  };
  readonly truck: VehicleConfig;
  /**
   * Rolling cable drum (user 2026-10-03: "dass sie auf einen zurollen … oder schräg über die Fahrbahn"): Ø 1.76 m,
   * rolls without a driver – no horn, no beacon, a short rumble as the warning.
   */
  readonly cableReel: VehicleConfig;
  /** Tower crane (VEH_CRANE_A) that carries a load: its trolley reaches this far from the mast. */
  readonly crane: { readonly minReach: number; readonly maxReach: number };
  /** Warning visuals and feedback (presentation). */
  readonly presentation: {
    readonly beaconFlashHz: number;
    /** Camera shake of an impact (block landing, heap dumped) at close range, fading out with distance. */
    readonly impactTrauma: number;
    readonly impactRange: number;
    /** Debris chunks falling from the bucket while dumping. */
    readonly debrisChunks: number;
  };
}

/** OBS_CRANE_LOAD_B: 1.6 × 1.0 × 1.2 m precast block on a four-leg chain sling, rope top 6.4 m above its underside. */
const CRANE_LOAD_B_RIG: CraneLoadRig = { pivotHeight: 6.4, loadBottom: 0, load: { width: 1.6, height: 1, depth: 1.2 }, guardHeight: 2.4 };

export const HAZARDS_CONFIG: HazardsConfig = {
  craneBeam: {
    // Two-leg sling (OBS_CraneLoad_A: chokers at ±2.0 m, hook ring 1.9 m above the beam tops): over the centre lane
    // the legs and the hook close 1.55 m above the beams – less than the runner, no jumping over there. Over the
    // outer lanes the legs have left the running line 0.15 m above the beam ends: the open sky above them can be
    // double-jumped (user 2026-10-02) – the guard covers the centre lane only.
    rig: { pivotHeight: 6.4, loadBottom: 0.02, load: { width: 6, height: 0.3, depth: 0.63 }, guardHeight: 2.2, guardHalfWidth: 1.1 },
    hoistedClearance: 5.2,
    telegraph: 0.9,
    lowerDuration: 1.3,
    readyLead: 1.4,
    swingDegrees: 9,
    // ≈ natural period of a 6.2 m pendulum
    swingPeriod: 5,
    idleSwayDegrees: 2,
    idleSwayPeriod: 6.5,
  },
  craneBlock: {
    rig: CRANE_LOAD_B_RIG,
    hoistedClearance: 5,
    loweredClearance: 0.3,
    telegraph: 1,
    dropDuration: 0.7,
    readyLead: 1.6,
    bounceHeight: 0.12,
    bounceDuration: 0.5,
    idleSwayDegrees: 2.5,
    idleSwayPeriod: 5.5,
    idleSpinDegreesPerSecond: 8,
  },
  craneSwing: {
    rig: CRANE_LOAD_B_RIG,
    hoistedClearance: 5,
    // Knee-high: the block visibly hangs free (its lowest corner stays ≥ 0.3 m above the road in the swing),
    // half a metre below the slide height 0.9 m – no sliding under it. The solid sling above the block reaches
    // past 3.8 m, the double-jump apex is 3.0 m – no jumping over it. Dodging is the only way.
    hangClearance: 0.4,
    telegraph: 1,
    lowerDuration: 1.5,
    // ≈ half a swing: lowered over the far side, the load sweeps across once in full view before the runner
    // arrives – far more than reaction + two lane changes (0.78 s). Warning 5 s ahead: 40 m at 8, 90 m at 18 m/s.
    readyLead: 2.5,
    // = lane width: the turning points lie over the outer lane centres, the load lingers there.
    reach: 2.2,
    trolleyTravel: 0.4,
    // ≈ natural period of the 5.9 m pendulum (rope top → centre of the block): 2π·√(5.9 / 9.81) = 4.87 s,
    // + 0.6 % for the 18° rope angle – a heavy load, slow at the turning points, fast over the centre lane.
    swingPeriod: 4.9,
    // The speed ramp brings the runner ≤ 0.06 s early (≈ 0.02 s per second at the warning, less later): 3 % keeps
    // up with it and changes the period unnoticeably; a hit holds the runner up ≥ 1.5 s – far outside the window.
    relock: { rate: 0.03, window: 0.25 },
    rebound: { height: 0.1, duration: 0.6 },
    twistDegrees: 5,
    twistPeriod: 3.7,
    wobbleDegrees: 1.5,
    wobblePeriod: 5.6,
    idleSwayDegrees: 2.5,
    idleSwayPeriod: 5.5,
    idleSpinDegreesPerSecond: 8,
    // Passing the load takes (runner 0.6 m + load ≤ 1.7 m along the track) / 8 m/s ≈ 0.29 s at the start
    // speed (± 0.14 s around the arrival), plus 0.1 s to spare (the relock keeps the phase on the arrival).
    blockWindow: 0.25,
  },
  excavator: {
    rig: {
      tailRadius: 2.8,
      upperHeight: 1,
      boomPivot: { forward: 1.25, up: 1, side: 0.25 },
      stickPivot: { forward: 4.05, up: 1.9 },
      bucketPivot: { forward: -0.292, up: -2.433 },
      bucket: { back: -0.64, front: 0.5, bottom: -1.45, top: 0.18, width: 1.1 },
    },
    telegraph: 0.8,
    swingIn: 1.4,
    lower: 0.6,
    dump: 0.6,
    heapDelay: 0.35,
    hold: 0.4,
    swingOut: 1.3,
    settle: 1,
    readyLead: 2,
    carryPivotHeight: 4.9,
    carryReach: 5,
    dumpPivotHeight: 3,
    carryCurlDegrees: 75,
    dumpCurlDegrees: -40,
    idle: {
      slewAwayDegrees: 120,
      reach: { min: 4.4, max: 5.6 },
      pivotHeight: { min: 1.4, max: 3.2 },
      curlDegrees: { min: 0, max: 70 },
      period: 5,
    },
    columnHeight: 2.6,
    heap: { width: 1.9, height: 0.75, depth: 1.7 },
  },
  forklift: {
    speed: 3.2,
    accelerationTime: 0.6,
    brakeDistance: 1.5,
    telegraph: 1,
    signalLead: 0.8,
    laneChangeLength: 5.5,
    maxSteerDegrees: 24,
    // VEH_FORKLIFT_A front to back: fork tines (0.12 m) to the tips, the wrapped pallet (1.12 m), the fork
    // carriage (1.32 m) in front of the mast, the cab under its overhead guard (2.26 m, mast 2.5 m), the
    // counterweight (1.2 m). Carriage parts rise with the forks (HIGH: +1.5 m).
    body: [
      { center: { x: 0, y: 0.075, z: 1.69 }, size: { x: 1.2, y: 0.15, z: 1.12 }, carriage: 'forks' },
      { center: { x: 0, y: 0.575, z: 1.57 }, size: { x: 1.2, y: 1.15, z: 0.8 }, carriage: 'load' },
      { center: { x: 0, y: 0.66, z: 1.055 }, size: { x: 1.0, y: 1.32, z: 0.15 }, carriage: 'forks' },
      { center: { x: 0, y: 1.15, z: 0.105 }, size: { x: 1.6, y: 2.3, z: 2.05 } },
      { center: { x: 0, y: 0.6, z: -1.11 }, size: { x: 1.6, y: 1.2, z: 0.38 } },
    ],
    front: 2.25,
    rear: 1.3,
    forks: { highLift: 1.5, loadTop: 1.15, tiltBackDegrees: 4 },
  },
  truck: {
    speed: 4.2,
    accelerationTime: 0.8,
    brakeDistance: 2.5,
    telegraph: 1,
    signalLead: 0.8,
    laneChangeLength: 7,
    maxSteerDegrees: 18,
    // One box: the cab (3.4 m, beacons 3.57 m) meets the runner first – nothing behind it can be jumped onto.
    body: [{ center: { x: 0, y: 1.8, z: 0 }, size: { x: 2.6, y: 3.6, z: 8.9 } }],
    front: 4.45,
    rear: 4.45,
  },
  cableReel: {
    speed: 3.5,
    accelerationTime: 0.4,
    brakeDistance: 1.2,
    telegraph: 0.8,
    signalLead: 0.5,
    // a slow diagonal across one lane, the drum turns only a little into it
    laneChangeLength: 9,
    maxSteerDegrees: 10,
    // drum Ø 1.76 m (axis across the lane, 1.43 m wide) – the same box as the standing OBS_CABLE_REEL_A
    body: [{ center: { x: 0, y: 0.87, z: 0 }, size: { x: 1.43, y: 1.74, z: 1.76 } }],
    front: 0.88,
    rear: 0.88,
  },
  crane: { minReach: 6, maxReach: 29 },
  presentation: {
    beaconFlashHz: 2.5,
    impactTrauma: 0.35,
    impactRange: 30,
    debrisChunks: 6,
  },
};
